import { ReactNode } from "react";
import { createPortal } from "react-dom";
import { useDashboardTitleSlots } from "./DashboardTitleSlots";
import { DashboardWidgetLiveBar } from "./DashboardWidgetLiveBar";

interface DashboardLiveWidgetShellProps {
    live: boolean;
    lastUpdatedAt: string | null;
    onRefresh: () => void;
    isRefreshing: boolean;
    showManualRefresh?: boolean;
    refreshLabel?: string;
    /** Last re-fetch error, shown inline on the live bar while stale content is kept. */
    error?: string | null;
    /**
     * Render the "Updated …" label + refresh button in the widget's title row
     * (when the widget has a title) instead of a footer band below the content.
     */
    barInTitle?: boolean;
    children: ReactNode;
}

/** Layout wrapper for dashboard widgets that support live refresh. */
export const DashboardLiveWidgetShell = ({
    live,
    lastUpdatedAt,
    onRefresh,
    isRefreshing,
    showManualRefresh,
    refreshLabel,
    error,
    barInTitle = false,
    children,
}: DashboardLiveWidgetShellProps) => {
    const titleSlots = useDashboardTitleSlots();
    const statusSlot = barInTitle ? titleSlots?.status ?? null : null;
    // In title mode, wait for the slot to mount instead of flashing the footer.
    const titleMode = barInTitle && titleSlots != null;
    const bar = (
        <DashboardWidgetLiveBar
            live={live}
            lastUpdatedAt={lastUpdatedAt}
            onRefresh={onRefresh}
            isRefreshing={isRefreshing}
            showManualRefresh={showManualRefresh}
            refreshLabel={refreshLabel}
            error={error}
            inTitle={titleMode}
        />
    );
    return (
        <div className="DashboardLiveWidget">
            <div className="DashboardLiveWidget__content">{children}</div>
            {titleMode ? (statusSlot ? createPortal(bar, statusSlot) : null) : bar}
        </div>
    );
};
